<template>
    <!--
    Passes up the native click event from the &lt;button&gt; element
    @event click
    @property {object} e - click event
  -->
    <component
        :is="isLink ? 'a' : 'button'"
        :class="className"
        v-bind="buttonProps"
        :disabled="disabled"
        @click.stop="e => $emit('click', e)"
    >
        <!-- @slot Button text, if no icon is present -->
        <slot>
            <RPLoadingIcon
                v-if="isSaving"
                :size="iconSizeComputed"
            />
            <StreamlineIcon
                v-if="isSuccess"
                class="RPButton__icon"
                :icon="successIcon"
                :fill="successColor"
                :size="iconSizeComputed"
            />
            <StreamlineIcon
                v-if="isError"
                class="RPButton__icon"
                :icon="errorIcon"
                :fill="errorColor"
                :stroke="whiteColor"
                :size="iconSizeComputed + 1"
            />

            <slot name="icon">
                <template v-if="showIcon">
                    <StreamlineIcon
                        class="RPButton__icon"
                        :icon="icon"
                        :stroke="iColor"
                        :fill="iFill"
                        :size="iconSizeComputed"
                    />
                </template>
            </slot>

            <template v-if="showLabel">
                <slot name="label">
                    <span
                        class="RPButton__text-container"
                    >{{ buttonText }}</span>
                </slot>
            </template>
        </slot>
    </component>
</template>

<script>
import StreamlineIcon from "@reviewpush/streamline-icons-vue";
import RPLoadingIcon from '../Misc/RPLoadingIcon.vue';
import isEmpty from 'lodash/isEmpty';
import stat from 'status';
import statusBuilder from 'status/statusBuilder';
import colors from 'style/_colors.scss';
import {CheckCircle1} from "@reviewpush/streamline-bold/lib/interface-essential/FormValidation";
import {Delete1} from "@reviewpush/streamline-regular/lib/interface-essential/Delete";

export default {
    name: 'RPButton',
    components: {
        StreamlineIcon,
        RPLoadingIcon
    },
    props: {
        /**
         * @values primary, secondary, tertiary
         */
        type: {
            type: String,
            default: 'primary',
            validator: val => ['primary', 'secondary', 'tertiary', 'active', 'error'].indexOf(val) !== -1,
        },
        /**
         * @values sm, md, lg, xl
         */
        size: {
            type: String,
            default: 'md',
            validator: val => ['sm', 'md', 'lg', 'xl'].indexOf(val) !== -1,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        /**
         * Expects a Streamline Icon component.
         * Instructions for importing individual Streamline components can be found in the Streamline app.
         */
        icon: {
            type: [Object, Array],
            default: () => ({}),
        },
        /**
         * Any valid CSS color
         */
        iconColor: {
            type: String,
            default: ''
        },
        /**
       * Any valid CSS color
       */
        iconFill: {
            type: String,
            default: 'transparent'
        },
        /**
         * The label controls the button text when not using the default slot. It must be used
         * to display button text when an icon is displayed.
         */
        label: {
            type: String,
            default: ''
        },
        /**
         * Set to `false` to only show the icon
         */
        showLabel: {
            type: Boolean,
            default: true
        },
        /**
         * A status pulled from the common `status` constants (`/src/status/index.js`)
         */
        status: {
            type: String,
            default: stat.STANDBY,
        },
        /**
         * Override the default icon size.
         */
        iconSize: {
            type: Number,
            default: null
        },
        /**
         * Replace the default button text when the button state is "Saving"
         */
        savingText: {
            type: String,
            default: 'Saving',
        },
        /**
         * Set the value of the button's `type` attribute. Used to prevent a button from
            submitting when inside a <form>
         */
        buttonType: {
            type: String,
            default: ''
        },
        /**
         * To render the component as a link (<a>), provide the `href` attribute.
         * Note: links have `display: inline-flex`, whereas buttons have `display: flex`.
         */
        href: {
            type: String,
            default: ''
        },
        /**
         * Only used when rendering as a link.
         */
        target: {
            type: String,
            default: '_self'
        },
    },
    data(){
        return {
            successIcon: CheckCircle1,
            errorIcon: Delete1,
            successColor: colors.success,
            errorColor: colors.error,
            whiteColor: colors.white,
        };
    },
    computed: {
        className() {
            return statusBuilder(
                'RPButton',
                this.status,
                false,
                {
                    [this.size]: true,
                    [this.type]: this.status === stat.STANDBY,
                    'fit': this.showIcon && !this.showLabel
                },
                true
            );
        },
        isSaving(){
            return this.status === stat.SAVING;
        },
        isSuccess(){
            return this.status === stat.SUCCESS;
        },
        isError(){
            return this.status === stat.ERROR;
        },
        showIcon(){
            return !this.isError
              && !this.isSuccess
              && !this.isSaving
              && !isEmpty(this.icon);
        },
        iColor(){
            let c = '';
            switch(this.status) {
            case stat.SUCCESS:
                c = colors.success;
                break;
            case stat.ERROR:
                c = colors.error;
                break;
            default:{
                c = this.type !== 'tertiary'
                    ? colors.white
                    : colors.grey;
                break;
            }
            }
            return this.iconColor === ''
                ? c
                : this.iconColor;
        },
        iFill(){
            let c = '';
            switch(this.status) {
            case stat.SUCCESS:
            case stat.ERROR:
            default:{
                c = colors.white;
                break;
            }
            }
            return this.iconFill === ''
                ? c
                : this.iconFill;
        },

        buttonText(){
            if(this.label !== ''){
                return this.label;
            }
            switch(this.status) {
            case stat.SAVING:
                return this.savingText;
            case stat.SUCCESS:
                return 'Success';
            case stat.ERROR:
                return 'Error';
            default:
                return 'Submit';
            }
        },
        iconSizeComputed(){
            if (this.iconSize) {
                return this.iconSize;
            }

            switch (this.size) {
            case 'xl':
                return 24;
            case 'lg':
                return 20;
            case 'md':
                return 16;
            default:
                return 12;
            }
        },
        isLink() {
            return this.href !== '';
        },
        buttonProps(){
            return this.isLink
                ? {href: this.href, target: this.target}
                : {type: this.buttonType};
        },
    },
};
</script>
<style lang="scss">
    .RPButton {
        cursor: pointer;
        font-weight: 600;
        border-radius: 0.25rem;
        padding: 0 1rem;
        display: flex;
        font-family: proxima-nova, sans-serif;
        align-items: center;
        justify-content: center;

        &:is(a) {
            &:hover{
                text-decoration: none;
            }
        }

        &--fit {
            padding: 0 0.5rem;
        }

        &--xl {
            height: 3rem;
        }
        &--lg {
            height: 2.5rem;
        }
        &--md {
            height: 2rem;
        }
        &--sm {
            height: 1.5rem;
        }

        &--primary {
            background-color: $primary;
            color: $white;
            border-width: 0;
            &:hover { background-color: $primary-400; }
            &:active { background-color: $primary-500; }
            &:disabled {
                cursor: default;
                background: $primary-100
            }
            &:focus:enabled {
                outline: none;
                box-shadow: 0 0 0.25rem $primary;
                border-radius: 0.25rem;
            }
        }
        &--secondary {
            background-color: $white;
            color: $primary;
            border-color: $primary;
            &:hover:enabled { background-color: $primary-50; }
            &:active:enabled { background-color: $primary-100; }
            &:active:disabled { border-style: solid; }
            &:disabled {
                box-shadow: none;
                cursor: default;
                color: $primary-100;
                border-color: $primary-100;
            }
            &:focus:enabled {
                outline: none;
                box-shadow: 0 0 0.25rem $primary;
                border-radius: 0.25rem;
            }
        }
        &--tertiary {
            background-color: $white;
            color: $grey;
            border: 1px solid $grey-200;
            &:active:enabled { background-color: $grey-100; }
            &:disabled {
                cursor: default;
                background: $white;
                color: $grey-100;
                border-color: $grey-100;
            }
            &:focus {
                outline: none;
                box-shadow: 0 0 0.25rem $primary;
                border-radius: 0.25rem;
            }
        }
        &--active {
            background-color: $white;
            color: $primary;
            border-color: $primary-200;
            &:active {
                background: $primary;
                color: white;
            }
        }

        &--success {
            color: $success-green;
            border-color:$success-green;
            background-color: $white;
        }
        &--error {
            background-color: $white;
            color: $error-red;
            border-width: 1px;
            border-color: $error-red;
            &:hover {
                background-color: $error-100;
            }
            &:disabled {
                cursor: default;
                border-color: $error-100;
                color: $error-100;
            }
        }
        &--saving {
            border: 1px solid $secondary-purple-300;
            background-color: $white;
            color: $secondary-purple-300;
        }

        &__text-container{
            white-space: nowrap;
            text-align: center;
            &:not(:first-child){
                margin-left: 0.5rem;
            }
        }
        &__icon {
            height: fit-content;
            margin: auto 0;
        }
    }
</style>

<docs>
Minimal example, using the default slot for the button text:

```vue
<RPButton>Button Text</RPButton>
```

You can use props to control the button's look and feel:

```vue
<RPButton
    type="secondary"
    :disabled="true"
    status="standby"
    size="xl"
>
    Button Text
</RPButton>
```

Buttons with icons:

```vue
import { House1 } from "@reviewpush/streamline-regular/lib/interface-essential/Home";

<RPButton
    :icon="House1"
    iconColor="#FFFFFF"
    :showLabel="false"
>
</RPButton>
<br>
<RPButton
    :icon="House1"
    iconColor="#FFFFFF"
    label="Go Home"
>
</RPButton>
```

Links with button styling:

```vue
<RPButton
    href="https://www.reviewpush.com"
    target="_blank"
>
    Visit ReviewPush!
</RPButton>

```
</docs>
